import { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { getLogs } from "../APIs/api-routes.util";
import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { EmptyState, LoadingOverlay } from "../components/ui/PagePrimitives";
import { apiSubscribe } from "../lib/api-subscribe";

interface LogRow {
  id: string;
  timeStamp: string;
  from: string;
  message: string;
}

function parseLogs(raw: unknown[]): LogRow[] {
  const logsData: LogRow[] = [];
  raw.forEach((element: unknown) => {
    const row = element as { post_body?: string; timestamp?: string };
    try {
      const log = JSON.parse(row.post_body || "{}");
      const msg = log?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
      if (msg) {
        logsData.push({
          id: `${row.timestamp}-${msg.from}-${msg.id || logsData.length}`,
          timeStamp: row.timestamp || "",
          from: msg.from,
          message: msg.text?.body || "—",
        });
      }
    } catch {
      /* skip malformed */
    }
  });
  return logsData;
}

function Logs() {
  const [data, setData] = useState<LogRow[]>([]);
  const [detail, setDetail] = useState<LogRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return apiSubscribe<unknown[]>(
      getLogs(),
      {
        next: (rows) => {
          setLoading(false);
          setData(parseLogs(rows || []));
        },
        error: () => {
          setLoading(false);
          setError("Could not load WhatsApp logs.");
        },
      }
    );
  }, []);

  useEffect(() => {
    const cleanup = load();
    return () => cleanup?.();
  }, [load]);

  if (detail) {
    return (
      <>
        <Button variant="secondary" size="sm" onClick={() => setDetail(null)}>
          ← Back to logs
        </Button>
        <div className="overflow-hidden rounded-xl border border-brand-100 bg-surface shadow-soft">
          <div className="bg-accent-blue-light/60 px-4 py-3">
            <p className="text-sm font-semibold text-accent-blue-dark">Message detail</p>
          </div>
          <dl className="divide-y divide-brand-100 px-4">
            <div className="space-y-1 py-3">
              <dt className="text-2xs font-semibold uppercase tracking-wide text-brand-600">Timestamp</dt>
              <dd className="text-sm text-content-primary">{detail.timeStamp}</dd>
            </div>
            <div className="space-y-1 py-3">
              <dt className="text-2xs font-semibold uppercase tracking-wide text-brand-600">From</dt>
              <dd className="text-sm font-medium text-accent-blue-dark">{detail.from}</dd>
            </div>
            <div className="space-y-1 py-3">
              <dt className="text-2xs font-semibold uppercase tracking-wide text-brand-600">Message</dt>
              <dd className="text-sm text-content-primary">{detail.message}</dd>
            </div>
          </dl>
        </div>
      </>
    );
  }

  return (
    <>
      <LoadingOverlay show={loading} />

      {error && (
        <Alert variant="error" onDismiss={() => setError("")}>
          {error}
        </Alert>
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="text-2xs text-content-muted">Incoming patient messages via the booking webhook.</p>
        <Button variant="secondary" size="sm" onClick={load} aria-label="Refresh logs">
          <FontAwesomeIcon icon={faRotateRight} />
        </Button>
      </div>

      {data.length === 0 && !loading && !error ? (
        <EmptyState title="No messages yet" description="WhatsApp webhook messages will appear here." />
      ) : (
        <ul className="divide-y divide-brand-100 overflow-hidden rounded-xl border border-brand-100 bg-surface shadow-soft">
          {data.map((row, i) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => setDetail(row)}
                className={`flex w-full flex-col gap-0.5 px-3 py-2.5 text-left transition-colors hover:bg-brand-50 ${
                  i % 2 === 0 ? "bg-accent-blue-light/15" : "bg-surface"
                }`}
              >
                <span className="text-2xs text-brand-600">{row.timeStamp}</span>
                <span className="text-sm font-semibold text-accent-blue-dark">{row.from}</span>
                <span className="line-clamp-2 text-2xs text-content-secondary">{row.message}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default Logs;
